import type { MessengerChatForm } from '@widgets/messenger/types'
import messengerApiService from '@widgets/messenger/services/messengerApiService'
import { useChatStore } from '@widgets/messenger/stores/useChatStore'
import { MessengerMessageStatus } from '@widgets/messenger/types/chat/message'
import type { MessengerChatRate } from '@widgets/messenger/types/chat/chat'
import { isChatEndedState } from '@widgets/messenger/types/chat/chat'
import { useWidgetStore } from '@widgets/messenger/stores/useWidgetStore'
import { useChatScroll } from '@widgets/messenger/compositions/useChatScroll'
import { isWidgetUploadedAttachment } from '@widgets/shared/types'
import { getUuid } from '@/utils/uuid'
import { computed } from '@/composition/vue/compositionApi'
import { WebWidgetChannelType } from '@/data/models/domain/messages/settings/webWidget/types'

export const useWidgetChat = () => {
  const chatStore = useChatStore()
  const { widgetSettings } = useWidgetStore()
  const { scrollToBottom } = useChatScroll()

  const hasChatChannel = computed(() => {
    return widgetSettings.value.channels[WebWidgetChannelType.CHAT].isActive
  })

  const isSatisfactionSurveyShown = computed(
    () => widgetSettings.value.channels[WebWidgetChannelType.CHAT].isSatisfactionSurveyShown,
  )

  const sendMessage = async (form: MessengerChatForm) => {
    const trimmedMessage = form.message.trim()
    const trackId = getUuid()

    chatStore.addMessageListItem({
      id: trackId,
      type: 'sent',
      messageTime: new Date().toISOString(),
      text: trimmedMessage,
      attachments: form.attachments.filter((attachment) => isWidgetUploadedAttachment(attachment)),
      status: MessengerMessageStatus.QUEUE,
      trackId,
    })
    setTimeout(() => {
      chatStore.resetNewMessageState()
      scrollToBottom()
    }, 0)
    try {
      await messengerApiService.sendMessage(chatStore.chatToken.value, {
        message: trimmedMessage,
        fileIds: form.attachments.map((attachment) => attachment.id),
        trackId,
      })
    } catch (e) {
      chatStore.patchMessageListItem(trackId, {
        status: MessengerMessageStatus.SENDING_ERROR,
      })
    }
  }

  const endChat = async () => {
    await messengerApiService.endChat(chatStore.chatToken.value)
    // Avoid waiting for websocket event
    chatStore.setChatState({
      type: 'ended',
    })
  }

  const sendSurveyRate = async (rate: MessengerChatRate) => {
    const chatState = chatStore.chatState.value
    if (!isChatEndedState(chatState)) {
      return
    }
    await messengerApiService.sendSurveyResult(chatStore.chatToken.value, { rate })
    chatStore.setChatState({
      ...chatState,
      rate,
    })
  }

  const sendSurveyComment = async (comment: string) => {
    const chatState = chatStore.chatState.value
    if (!isChatEndedState(chatState)) {
      return
    }
    await messengerApiService.sendSurveyResult(chatStore.chatToken.value, { comment })
    chatStore.setChatState({
      ...chatState,
      comment,
    })
  }

  return {
    chatState: chatStore.chatState,
    userEmail: chatStore.userEmail,
    messageListItems: chatStore.messageListItems,
    agentTypingCount: chatStore.agentTypingCount,
    hasChatChannel,
    isSatisfactionSurveyShown,
    sendMessage,
    endChat,
    sendSurveyRate,
    sendSurveyComment,
  }
}
